import { useContext, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@material-ui/core';
import { FiMessageCircle } from 'react-icons/fi';

import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { User } from '@vyce/core/src/types';
import { sendFormMessageRequest } from '@vyce/core/src/api/forms';
import { ConfirmDialog } from '@vyce/core/src/components';

import { FormRecipientsTable } from './FormRecipientsTable';

interface Props {
  open: boolean;
  onClose: () => void;
  type: 'recipients' | 'responses' | 'outstanding';
  companyId: string;
  formId: string;
  user: User;
}

export const FormRecipientsDialog: React.FC<Props> = ({ open, onClose, type, companyId, formId, user }) => {
  const { isMobile } = useContext(DeviceContext);
  const { showNotification } = useContext(NotificationContext);
  const [openResendAllDialog, setOpenResendAllDialog] = useState(false);
  const [loadingResendAll, setLoadingResendAll] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const title = type === 'recipients' ? 'Recipients' : type === 'responses' ? 'Responses' : 'Outstanding';

  const handleResendAll = async () => {
    if (!companyId || !formId || !user.uuid) return;
    try {
      setLoadingResendAll(true);
      await sendFormMessageRequest({
        companyId,
        formId,
        userId: user.uuid,
        sendToAll: true,
      });
      showNotification({ message: 'Form has been sent', options: { variant: 'success' } });
      setOpenResendAllDialog(false);
      setReloadKey(key => key + 1);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingResendAll(false);
    }
  };

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      maxWidth="lg"
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={16}>
          {title}
          {type === 'outstanding' && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<FiMessageCircle size={14} />}
              onClick={() => setOpenResendAllDialog(true)}
            >
              Resend All
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent style={{ minWidth: isMobile ? '100%' : 1000 }}>
        {open && (
          <FormRecipientsTable
            type={type}
            companyId={companyId}
            formId={formId}
            user={user}
            reloadKey={reloadKey}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button size="large" variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={openResendAllDialog}
        handleClose={() => setOpenResendAllDialog(false)}
        handleConfirm={handleResendAll}
        loading={loadingResendAll}
        confirmText="Resend"
        cancelText="Cancel"
        title="Are you sure you want to resend all messages to all recipients, who haven't filled the form yet?"
      />
    </Dialog>
  );
};
